import { useTranslation } from "react-i18next";
import { ViewIcon } from "@/components/icons/action-icons";
import FiltersModal from "@/components/FiltersModal";
import { usePolicyAcknowledgementParams } from "./acknowledgement.params";
import FilterButton from "@/components/table/FilterButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table } from "@/components/table";
import { format, format as formatDate } from "date-fns";
import { startCase } from "lodash";
import { BellRing, CheckCircle2, EyeOff, UserIcon } from "lucide-react";
import { useMemo, useState, type ElementType } from "react";
import type { TFunction } from "i18next";
import {
  ENUM_COMPANY_POLICY_VIEWED_STATUS,
  ENUM_MS_COMAPNY_POLICIES_STATUS,
} from "@/utils/enums/company-policies.enum";
import { useParams } from "react-router-dom";
import MySpaceCompanyPoliciesFilters from "./policy-acknowledgement-filters";
import { useGetAllAcknowledgedCompanyPoliciesQuery } from "@/services/(company)/api.company-policies";
import type { IPolicyAcknowledgementUser } from "@/types/(company)/company-policies.interface";
import { PrimaryDashedSvg } from "@/assets/svgs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const buildColumns = (t: TFunction) => [
  {
    key: "employeeName",
    label: t("company:policies_settings.acknowledgement_table_employee_name"),
  },
  {
    key: "email",
    label: t("common:labels.email"),
  },
  {
    key: "viewedOn",
    label: t("company:policies_settings.viewed_on"),
  },
  {
    key: "acknowledgedOn",
    label: t("company:policies_settings.acknowledged_on"),
  },
  {
    key: "status",
    label: t("common:labels.status"),
  },
  {
    key: "actions",
    label: t("common:labels.actions"),
    className: "justify-end",
  },
];

export const PolicyAcknowledgementTable = () => {
  const { t } = useTranslation(["common", "company"]);
  const columns = useMemo(() => buildColumns(t), [t]);
  const { companyPoliciesId } = useParams();
  // Search, filters and page all live in the URL — see useTableParams.
  const tp = usePolicyAcknowledgementParams();
  const filters = tp.filters;
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const toISODate = (date?: Date | null) =>
    date ? format(date, "yyyy-MM-dd") : undefined;

  const activeParams = useMemo(
    () => ({
      ...(filters.statusLabel ? { statusLabel: filters.statusLabel } : {}),
      ...(filters.acknowledgedOnFrom
        ? { acknowledgedOnFrom: toISODate(filters.acknowledgedOnFrom) }
        : {}),
      ...(filters.acknowledgedOnTo
        ? { acknowledgedOnTo: toISODate(filters.acknowledgedOnTo) }
        : {}),
      ...(filters.viewedOnFrom
        ? { viewedOnFrom: toISODate(filters.viewedOnFrom) }
        : {}),
      ...(filters.viewedOnTo
        ? { viewedOnTo: toISODate(filters.viewedOnTo) }
        : {}),
    }),
    [filters],
  );

  const {
    data: acknowledgedCompanyPolicies,
    isLoading,
    isFetching,
  } = useGetAllAcknowledgedCompanyPoliciesQuery(
    {
      companyPoliciesId: String(companyPoliciesId),
      limit: 10,
      search: tp.committedSearch,
      page: tp.page,
      ...activeParams,
    },
    {
      skip: !companyPoliciesId,
      refetchOnMountOrArgChange: true,
    },
  );

  const rows =
    acknowledgedCompanyPolicies?.data &&
    Array.isArray(acknowledgedCompanyPolicies.data)
      ? (acknowledgedCompanyPolicies.data as IPolicyAcknowledgementUser[]).map(
          (acknowledgedCompanyPolicy: IPolicyAcknowledgementUser) => ({
            employeeName: (
              <div className="flex items-center gap-2 w-full">
                <Avatar className="size-8 shrink-0">
                  <AvatarImage
                    src={acknowledgedCompanyPolicy.profileImage || undefined}
                  />
                  <AvatarFallback className="bg-border">
                    <UserIcon size={12} className="text-gray-500" />
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">
                  {acknowledgedCompanyPolicy.employeeName}
                </span>
              </div>
            ),
            email: acknowledgedCompanyPolicy.email ?? "-",
            viewedOn: acknowledgedCompanyPolicy.viewedOn
              ? formatDate(acknowledgedCompanyPolicy.viewedOn, "MMM dd, yyyy")
              : "-",
            acknowledgedOn: acknowledgedCompanyPolicy.acknowledgedOn
              ? formatDate(
                  acknowledgedCompanyPolicy.acknowledgedOn,
                  "MMM dd, yyyy",
                )
              : "-",
            status: <StatusPill status={acknowledgedCompanyPolicy.status} />,
            actions: (
              <div className="flex items-center  justify-end gap-2 w-36">
                <div className="flex gap-1 items-center rounded-full px-2 py-1.5 text-xs bg-gray-500/10 text-gray-500">
                  {acknowledgedCompanyPolicy.viewedStatus ===
                  ENUM_COMPANY_POLICY_VIEWED_STATUS.VIEWED ? (
                    <ViewIcon size={18} />
                  ) : (
                    <EyeOff size={18} />
                  )}
                  <span className="no-underline">
                    {acknowledgedCompanyPolicy.viewedStatus}
                  </span>
                </div>
                <Button
                  size="icon"
                  variant="link"
                  aria-label={t("common:a11y.send_reminder")}
                  data-tooltip-id="hrm-tooltip"
                  data-tooltip-content={t("common:a11y.send_reminder")}
                  onClick={() => {}}
                >
                  <BellRing size={18} />
                </Button>
              </div>
            ),
          }),
        )
      : [];

  return (
    <div className="w-full relative rounded-sm border h-fit p-4 ">
      <Table
        data={rows}
        loading={isLoading || isFetching}
        searchText={tp.search}
        columns={columns}
        pagination={{
          totalPages: acknowledgedCompanyPolicies?.meta?.totalPages ?? 1,
          currentPage: tp.page,
          onPageChange: tp.setPage,
        }}
        onSearchChange={tp.setSearch}
        onResetFilters={tp.hasActive ? tp.reset : undefined}
        searchPlaceholder={t(
          "company:policies_settings.acknowledgement_table_search_placeholder",
        )}
        filters={[
          {
            type: "custom",
            key: "filter",
            render: () => (
              <FilterButton
                filtersCount={tp.activeCount}
                onClick={() => setIsFilterModalOpen(true)}
                onReset={tp.reset}
              />
            ),
          },
        ]}
      />
      <FiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        initialFilters={tp.filters}
        defaultFilters={tp.defaults}
        onApply={(newFilters) => {
          tp.applyFilters(newFilters);
          setIsFilterModalOpen(false);
        }}
        renderFilters={({ filters, onChange }) => (
          <MySpaceCompanyPoliciesFilters
            filters={filters}
            onChange={onChange}
          />
        )}
      />
    </div>
  );
};

const StatusPill = ({
  status,
}: {
  status: ENUM_MS_COMAPNY_POLICIES_STATUS;
}) => {
  const statusPills: Record<
    ENUM_MS_COMAPNY_POLICIES_STATUS,
    { icon: ElementType; className: string }
  > = {
    [ENUM_MS_COMAPNY_POLICIES_STATUS.ACKNOWLEDGED]: {
      icon: CheckCircle2,
      className: "bg-green-100 text-green-800",
    },
    [ENUM_MS_COMAPNY_POLICIES_STATUS.OVERDUE]: {
      icon: PrimaryDashedSvg,
      className: "bg-red-200 text-red-600",
    },
    [ENUM_MS_COMAPNY_POLICIES_STATUS.PENDING]: {
      icon: PrimaryDashedSvg,
      className: "bg-orange-100 text-orange-800",
    },
  };
  const fallback = {
    icon: PrimaryDashedSvg,
    className: "bg-gray-500/10 text-gray-500",
  };
  const { icon: Icon, className } =
    statusPills[status as ENUM_MS_COMAPNY_POLICIES_STATUS] ?? fallback;

  return (
    <Badge className={`font-medium ${className}`}>
      {Icon && <Icon className="size-4 me-1" />}
      {startCase(status)}
    </Badge>
  );
};
